/**
 * Depth-preview registry — the data model for the in-tool "paywall-the-depth" pattern.
 *
 * Each report SKU declares only the paid report section LABELS. The OfferPlacement `gate`
 * renders those labels with one shared localized description above the unlock CTA.
 * Because the preview describes paid-only depth (not free output and not the real report
 * body), it is NET-ZERO: nothing currently-free is given away, and the paid depth is
 * shown to EXIST without being revealed through the neutral premium-preview pattern.
 *
 * Generalizes to every report SKU: add a `planId` entry and the same component renders
 * it. The free tool surface keeps rendering its existing free sections unchanged.
 */

type DepthPreviewSection = { label: string };
export type DepthPreview = { sections: DepthPreviewSection[] };

const DEPTH_PREVIEWS: Record<string, { en: DepthPreview; hi: DepthPreview }> = {
  brihat_kundali: {
    en: {
      sections: [
        {
          label: "Your Vimshottari Dasha timeline",
        },
        {
          label: "Yogas & Raj Yogas in your chart",
        },
        {
          label: "All doshas with severity & remedies",
        },
        {
          label: "Your year ahead (transit forecast)",
        },
      ],
    },
    hi: {
      sections: [
        {
          label: "आपकी विंशोत्तरी दशा का समय",
        },
        {
          label: "आपकी कुंडली के योग और राज योग",
        },
        {
          label: "सभी दोष — तीव्रता और उपाय सहित",
        },
        {
          label: "आपका आने वाला साल (गोचर फल)",
        },
      ],
    },
  },
  marriage_report: {
    en: {
      sections: [
        {
          label: "Full 36-guna Gun Milan breakdown",
        },
        {
          label: "Mangal / Nadi / Bhakoot dosha — severity & cancellation",
        },
        {
          label: "Your partner & the 7th house",
        },
        {
          label: "Marriage timing & remedies",
        },
      ],
    },
    hi: {
      sections: [
        {
          label: "पूरा 36-गुण गुण मिलान विश्लेषण",
        },
        {
          label: "मंगल / नाड़ी / भकूट दोष — तीव्रता और भंग",
        },
        {
          label: "आपका जीवनसाथी और सप्तम भाव",
        },
        {
          label: "विवाह का समय और उपाय",
        },
      ],
    },
  },
  muhurat_report: {
    en: {
      sections: [
        {
          label: "Ranked shubh windows for your purpose",
        },
        {
          label: "Choghadiya, Abhijit & the times to avoid",
        },
        {
          label: "Your sankalp & remedy timing",
        },
      ],
    },
    hi: {
      sections: [
        {
          label: "आपके कार्य के लिए श्रेष्ठ शुभ मुहूर्त",
        },
        {
          label: "चौघड़िया, अभिजित और बचने का समय",
        },
        {
          label: "आपका संकल्प और उपाय का समय",
        },
      ],
    },
  },
  dosha_remedy_plan: {
    en: {
      sections: [
        {
          label: "Your dosha graded — and does it cancel?",
        },
        {
          label: "The root cause in your chart",
        },
        {
          label: "Step-by-step upay",
        },
        {
          label: "When to begin the remedy",
        },
      ],
    },
    hi: {
      sections: [
        {
          label: "आपका दोष — तीव्रता और भंग",
        },
        {
          label: "आपकी कुंडली में मूल कारण",
        },
        {
          label: "चरण-दर-चरण उपाय",
        },
        {
          label: "उपाय कब शुरू करें",
        },
      ],
    },
  },
  mahadasha_forecast: {
    en: {
      sections: [
        {
          label: "Your current & upcoming dasha periods",
        },
        {
          label: "What each period turns on",
        },
        {
          label: "Caution & opportunity windows + remedies",
        },
      ],
    },
    hi: {
      sections: [
        {
          label: "आपकी वर्तमान और आने वाली दशाएँ",
        },
        {
          label: "हर काल क्या सक्रिय करता है",
        },
        {
          label: "सावधानी और अवसर के समय + उपाय",
        },
      ],
    },
  },
  annual_forecast: {
    en: {
      sections: [
        {
          label: "Month-by-month for the next 12",
        },
        {
          label: "Career, money, relationship & health windows",
        },
        {
          label: "Saturn & Jupiter transit effects + remedies",
        },
      ],
    },
    hi: {
      sections: [
        {
          label: "अगले 12 महीने — महीना दर महीना",
        },
        {
          label: "करियर, धन, रिश्ते और स्वास्थ्य के समय",
        },
        {
          label: "शनि और गुरु का गोचर फल + उपाय",
        },
      ],
    },
  },
  career_report: {
    en: {
      sections: [
        {
          label: "Your 10th house & career lord",
        },
        {
          label: "Best-fit fields & timing",
        },
        {
          label: "Money & growth windows + remedies",
        },
      ],
    },
    hi: {
      sections: [
        {
          label: "आपका दशम भाव और कर्म स्वामी",
        },
        {
          label: "अनुकूल क्षेत्र और समय",
        },
        {
          label: "धन और उन्नति के समय + उपाय",
        },
      ],
    },
  },
  sade_sati: {
    en: {
      sections: [
        {
          label: "Where you are in Sade Sati right now",
        },
        {
          label: "What each 2.5-year phase brings",
        },
        {
          label: "Shani upay & the windows that ease it",
        },
      ],
    },
    hi: {
      sections: [
        {
          label: "अभी आप साढ़े साती में कहाँ हैं",
        },
        {
          label: "हर ढाई साल का चरण क्या लाता है",
        },
        {
          label: "शनि उपाय और राहत के समय",
        },
      ],
    },
  },
  pitra_dosh: {
    en: {
      sections: [
        {
          label: "Is Pitra Dosh really in your chart — and how strong",
        },
        {
          label: "The placement that forms it",
        },
        {
          label: "The shraddh & remedy plan + timing",
        },
      ],
    },
    hi: {
      sections: [
        {
          label: "क्या आपकी कुंडली में सच में पितृ दोष है — और कितना प्रबल",
        },
        {
          label: "वह स्थिति जो इसे बनाती है",
        },
        {
          label: "श्राद्ध और उपाय योजना + समय",
        },
      ],
    },
  },
};

export function depthPreviewFor(planId: string, language: string): DepthPreview | null {
  const entry = DEPTH_PREVIEWS[planId];
  if (!entry) return null;
  return language === "hi" ? entry.hi : entry.en;
}
